import { inject, Injectable } from '@angular/core'
import { DocumentGedDto } from '../data-access/http/dto/document-ged.dto'
import { catchError, EMPTY, first, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs'
import { PieceJointeDto } from '../data-access/http/dto/piece-jointe.dto'
import { DOCUMENT, formatDate } from '@angular/common'
import { PieceJointeDomainService } from './piece-jointe.domain'
import { RetourEnveloppeDocusignDto } from '../data-access/http/dto/retour-enveloppe-docusign.dto'
import { ConfirmationModalComponent } from '../ui/confirmation-modal/confirmation-modal.component'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { DocumentationState } from '../data-access/state/documentation.state'
import { BrouillonHttpService } from '../data-access/http/brouillon-http.service'
import {
    DocumentUploadModalFormValue,
    UploadDriveLinkModalComponent,
} from '../ui/upload-drive-link-modal/upload-drive-link-modal.component'
import { TypeDocumentCode } from '../models/enums/types-document'
import { ChantierDomainService } from './chantier.domain'
import { DocumentGedHttpService } from '../data-access/http/document-ged-http.service'
import { GlobalDomainService } from './global.domain'
import { ToastrService } from 'ngx-toastr'
import { BrouillonIfExistDto } from '../data-access/http/dto/brouillon-if-exist-dto'
import { BrouillonDto } from '../data-access/http/dto/brouillon-dto'
import { Router } from '@angular/router'
import { EvenementDomainService } from './evenement.domain'

@Injectable({ providedIn: 'root' })
export class DocumentationDomainService {
    private readonly document = inject(DOCUMENT)

    private readonly pieceJointeDomainService = inject(PieceJointeDomainService)
    private readonly chantierDomainService = inject(ChantierDomainService)
    private readonly globalDomainService = inject(GlobalDomainService)
    private readonly documentGedHttpService = inject(DocumentGedHttpService)
    private readonly modalService = inject(NgbModal)
    private readonly evenementDomainService = inject(EvenementDomainService)
    private readonly documentationState = inject(DocumentationState)
    private readonly router = inject(Router)

    // Selectors
    brouillonCounter$: Observable<number> = this.documentationState.store.select(DocumentationState.getBrouillonCounter)
    private readonly brouillonHttpService = inject(BrouillonHttpService)
    private readonly toastr = inject(ToastrService)

    // Actions
    openDocument(documentGedDto: DocumentGedDto): Observable<[Blob, PieceJointeDto]> {
        if (documentGedDto.lienDocumentDrive) {
            return of(documentGedDto).pipe(
                tap((document) => this.document.defaultView.open(document.lienDocumentDrive)),
                map((): [null, null] => [null, null]),
            )
        }
        return this.pieceJointeDomainService.downloadPieceJointeById(documentGedDto.document_id)
    }

    startDocumentGenerationProcedure(
        generatePdf: Observable<Blob>,
        generateDocusignEnveloppe: Observable<RetourEnveloppeDocusignDto>,
        config?: ProcedureSignatureConfig,
    ): Observable<Blob | RetourEnveloppeDocusignDto> {
        const modalRef = this.modalService.open(ConfirmationModalComponent)
        const modal: ConfirmationModalComponent = modalRef.componentInstance

        modal.modalTitle = 'Signature électronique'
        modal.confirmationText = 'Souhaitez-vous une signature électronique ?'
        modal.highlightConfirm = true
        modal.confirmButtonLabel = 'OUI'
        modal.cancelButtonLabel = 'Non, je souhaite imprimer le PDF'

        modal.tooltip =
            'Le document signé en signature électronique sera envoyé aux signataires, stocké dans portail chantier et imprimable si besoin'

        const modalClosed$ = modalRef.closed.pipe(first())
        return modalClosed$.pipe(
            switchMap((reason) => {
                if (reason === 'cancel') {
                    return generatePdf
                }
                if (reason === 'confirm') {
                    if (config?.showEmailWarning) {
                        const emailModalRef = this.modalService.open(ConfirmationModalComponent)
                        const emailModal: ConfirmationModalComponent = emailModalRef.componentInstance

                        emailModal.modalTitle = 'Signature électronique'
                        emailModal.confirmationText = `L'adresse mail du signataire ${config.signataireDisplayName} n’a pas été renseignée. Aucune copie ne pourra lui être envoyée.`
                        emailModal.confirmButtonLabel = 'Ajouter une adresse email avant de signer'
                        emailModal.cancelButtonLabel = 'Continuez quand même et imprimer plus tard'
                        emailModal.highlightConfirm = true

                        return emailModalRef.closed.pipe(first())
                    }
                    return of('cancel')
                }
                return EMPTY
            }),
            switchMap((reason: string) => {
                if (reason === 'confirm') {
                    config?.setFocusOnEmailInput()
                    return EMPTY
                }
                if (reason === 'cancel') {
                    return generateDocusignEnveloppe
                }
                return EMPTY
            }),
        )
    }

    loadBrouillonCountForSelectedChantier(): Observable<number> {
        return this.brouillonHttpService.getBrouillonCount(this.chantierDomainService.selectedChantierCode()).pipe(
            tap((brouillonCounter) => {
                this.documentationState.store.update((state) => ({
                    ...state,
                    brouillonCounter: brouillonCounter,
                }))
            }),
        )
    }

    setBrouillonCount(brouillonCounter: number): void {
        this.documentationState.store.update((state) => ({
            ...state,
            brouillonCounter,
        }))
    }

    uploadDocumentDrive(): Observable<DocumentGedDto> {
        const codeChantier = this.chantierDomainService.selectedChantierCode()
        const codeTypeDocumentGed: TypeDocumentCode = 'LIEN_DRIVE'
        const modalRef = this.modalService.open(UploadDriveLinkModalComponent)
        const modalComponent = modalRef.componentInstance as UploadDriveLinkModalComponent

        return modalComponent.submitForm.pipe(
            first(),
            switchMap((formValue: DocumentUploadModalFormValue) =>
                this.documentGedHttpService.saveDocument({
                    fileName: formValue.fileName,
                    lienDocumentDrive: formValue.lienDocumentDrive,
                    codeChantier,
                    codeTypeDocumentGed,
                }),
            ),
            catchError(() => {
                this.globalDomainService.stopSpinner()
                return EMPTY
            }),
            tap((documentGedDto) => {
                this.globalDomainService.stopSpinner()
                this.toastr.success(`Le document ${documentGedDto.nomDocumentDrive} a bien été ajouté`)
            }),
        )
    }

    checkIfExistBrouillon(
        codeTypeDocumentGed: string,
        codeChantier: string,
        matriculeSalarie: string,
        matriculeInterimaire: string,
    ): Observable<number> {
        const params: BrouillonIfExistDto = {
            codeTypeDocumentGed,
            codeChantier,
            matriculeSalarie,
            matriculeInterimaire,
        }

        const getInfosDocumentById = (
            id: string,
        ): {
            typeDocumentNom: string
            modalTitle: string
            cancelButtonLabel: string
        } => {
            if (id === '1') {
                return {
                    typeDocumentNom: 'du registre d’accueil sécurité',
                    modalTitle: "Registre d'accueil sécurité",
                    cancelButtonLabel: 'Démarrer un nouveau registre sécurité',
                }
            }
            if (id === '2') {
                return {
                    typeDocumentNom: 'd’une autorisation de conduite',
                    modalTitle: 'Autorisation de conduite',
                    cancelButtonLabel: 'Démarrer une nouvelle autorisation de conduite',
                }
            }
            return null
        }

        return this.brouillonHttpService.getBrouillonIfExist(params).pipe(
            switchMap((brouillon: BrouillonDto) => {
                if (!brouillon?.id) {
                    return of({ reason: 'cancel', brouillon: null })
                }
                const documentInfos = getInfosDocumentById(brouillon.typeDocumentGed_id)
                const modalRef = this.modalService.open(ConfirmationModalComponent)
                const modalComponent: ConfirmationModalComponent = modalRef.componentInstance

                const prenom = brouillon.interimaire_prenom || brouillon.salarie_prenom
                const nom = brouillon.interimaire_nom || brouillon.salarie_nom
                const typeDocumentNom = documentInfos.typeDocumentNom
                const date = formatDate(new Date(brouillon.modifiedDate), 'dd/MM/yyyy', 'FR-fr')

                modalComponent.modalTitle = documentInfos.modalTitle
                modalComponent.confirmationText = `Un brouillon ${typeDocumentNom} existe pour ${prenom} ${nom} depuis le ${date}. Voulez-vous utiliser ce brouillon ou démarrer un formulaire vide ?`
                modalComponent.confirmButtonLabel = 'Utiliser le Brouillon'
                modalComponent.cancelButtonLabel = documentInfos.cancelButtonLabel

                return modalRef.closed.pipe(map((reason) => ({ reason, brouillon })))
            }),
            switchMap(({ reason, brouillon }) => {
                if (reason === 'cancel') {
                    return forkJoin([
                        brouillon?.id
                            ? this.brouillonHttpService.deleteBrouillonById(brouillon.id).pipe(map((): null => null))
                            : of(null),
                        this.evenementDomainService.createClickAnnulerBrouillon(
                            codeChantier,
                            codeTypeDocumentGed,
                            matriculeSalarie,
                            matriculeInterimaire,
                        ),
                    ]).pipe(map((): null => null))
                }

                if (reason === 'confirm') {
                    return this.evenementDomainService
                        .createClickUtiliserBrouillon(
                            codeChantier,
                            codeTypeDocumentGed,
                            matriculeSalarie,
                            matriculeInterimaire,
                        )
                        .pipe(map(() => brouillon.id))
                }
                if (reason === 'close') {
                    this.router.navigate(['/accueil-chantier'])
                }
                return EMPTY
            }),
        )
    }
}

export interface ProcedureSignatureConfig {
    showEmailWarning: boolean
    setFocusOnEmailInput: () => void
    signataireDisplayName: string
}
